import React, { forwardRef } from 'react';

import { observable } from 'mobx';

import { UniqueIdentifier } from '@dnd-kit/core';

import { AnimateLayoutChanges, useSortable, defaultAnimateLayoutChanges } from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import { Collapse } from '@mui/material';

import Box from '@mui/material/Box';

import { colors } from 'theme/colors';

import { Handle } from '../helpers/Handle';
import { ContainerProps } from './types';
import { Container as StyledContainer, Actions } from './styles';
import ContainerHeader from '../ContainerHeader/ContainerHeader';

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

export function DroppableContainer({
  children,
  disabled,
  fixed,
  id,
  items,
  withoutHeader = false,
  ...props
}: ContainerProps & {
  disabled?: boolean;
  id: UniqueIdentifier;
  items: UniqueIdentifier[];
  style?: React.CSSProperties;
}): React.ReactElement {
  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id,
    data: {
      type: 'container',
      children: items,
    },
    // A fixed section is neither dragged nor dropped into, so dnd-kit never registers it as either.
    disabled: { draggable: Boolean(fixed), droppable: Boolean(fixed) },
    animateLayoutChanges,
  });

  return (
    <Container
      ref={disabled ? undefined : setNodeRef}
      style={{
        transition,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
        border: isDragging ? `1px solid ${colors.primary.main}` : undefined,
      }}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      withoutHeader={withoutHeader}
      fixed={fixed}
      id={id}
      {...props}
    >
      {children}
    </Container>
  );
}

const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      label,
      handleProps,
      onClick,
      onRemove,
      disableActionsHeader: disableActions,
      withoutHeader,
      isActive,
      fixed,
      fixedTooltip,
      id,
      ...props
    }: ContainerProps,
    ref
  ) => {
    const [expanded, setExpanded] = React.useState(true);

    return (
      <StyledContainer
        {...props}
        ref={ref}
        onClick={onClick}
        tabIndex={onClick ? 0 : undefined}
        $withoutHeader={withoutHeader}
        $isActive={isActive}
      >
        {!disableActions && !fixed && (
          <Actions $isActive={isActive}>
            <Handle {...handleProps} />
          </Actions>
        )}
        <Box sx={{ flex: 1 }} id={id || `section-container-${label}`}>
          {!withoutHeader && (
            <ContainerHeader
              expanded={expanded}
              setExpanded={setExpanded}
              label={label}
              onRemove={onRemove}
              fixed={fixed}
              fixedTooltip={fixedTooltip}
              id={id}
            />
          )}
          <Collapse
            in={expanded}
            style={{
              margin: expanded ? '6px' : '',
            }}
          >
            {children}
          </Collapse>
        </Box>
      </StyledContainer>
    );
  }
);

export default observable(Container);
